import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import baseUrl from "../../../../../api/baseUrl";

type AdminAuthModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onVerified: () => void;
};

function AdminAuthModal({ isOpen, onCancel, onVerified }: AdminAuthModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setPassword("");
      setErrorMessage("");
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage("");

    try {
      await baseUrl.post("/branch/pos/verify-admin", { email, password });
      onVerified();
    } catch (error) {
      console.error("Error verifying admin credentials:", error);
      setErrorMessage("Invalid admin credentials.");
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEmail("");
    setPassword("");
    setErrorMessage("");
    onCancel();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !submitting) {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-erp-navy/60 p-4 backdrop-blur-[2px]"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-[420px] overflow-hidden rounded-2xl border border-erp-line bg-white font-inter shadow-[0_24px_60px_rgba(11,28,44,0.3)]">
        <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-erp-gold/40 bg-erp-gold/20 text-erp-gold">
              <LockIcon className="!text-[18px]" />
            </div>
            <div>
              <h2 className="m-0 text-[15px] font-bold text-white">
                Admin Verification
              </h2>
              <p className="m-0 mt-0.5 text-[11px] text-white/60">
                Required to open a sales return
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="inline-flex rounded-lg border border-white/15 bg-white/10 p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
          >
            <CloseIcon className="!text-[18px]" />
          </button>
        </div>

        <div className="space-y-3.5 p-5">
          <p className="m-0 text-[12px] leading-relaxed text-erp-muted">
            Enter admin credentials to open and edit this bill for a sales
            return.
          </p>

          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.05em] text-erp-muted">
              Admin Email
            </label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              className="h-11 w-full rounded-[10px] border border-erp-line bg-erp-surface px-3 text-[13px] font-medium text-erp-ink outline-none transition-[border-color,box-shadow,background] placeholder:text-[#8ea3b8] focus:border-erp-accent focus:bg-white focus:shadow-[0_0_0_3px_rgba(31,111,139,0.14)]"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.05em] text-erp-muted">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              className="h-11 w-full rounded-[10px] border border-erp-line bg-erp-surface px-3 text-[13px] font-medium text-erp-ink outline-none transition-[border-color,box-shadow,background] placeholder:text-[#8ea3b8] focus:border-erp-accent focus:bg-white focus:shadow-[0_0_0_3px_rgba(31,111,139,0.14)]"
              placeholder="Enter password"
            />
          </div>

          {errorMessage ? (
            <div className="rounded-lg border border-erp-danger/25 bg-erp-danger/10 px-3 py-2 text-[12px] font-semibold text-erp-danger">
              {errorMessage}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2.5 border-t border-erp-line px-5 py-3.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-erp-line bg-white px-4 text-[12px] font-bold text-erp-muted transition-colors hover:border-erp-muted hover:text-erp-ink disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !email || !password}
            className="inline-flex h-10 min-w-[96px] items-center justify-center rounded-lg border border-erp-navy bg-erp-navy px-4 text-[12px] font-bold text-white shadow-[0_4px_12px_rgba(11,28,44,0.16)] transition-colors hover:bg-erp-navy-mid disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Verifying..." : "Verify"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default AdminAuthModal;
